import { DialogBody, DialogCloseTrigger, DialogContent, DialogHeader, DialogRoot } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { dailySpinChainId } from '@/constants/shared';
import { DAILY_SPIN_ABI } from '@/network/abi/daily-spin';
import { RewardCategory } from '@/types';
import { formatNumberToLocale } from '@/utils';
import { Card, Flex, Heading, HStack, Image, Separator, Text, VStack } from '@chakra-ui/react';
import { LottieReact } from '@lottielab/lottie-player';
import { useReadContract } from 'wagmi';
import { REWARD_CATEGORY_METADATA } from './constants';

export const AboutRewardsModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { data, isLoading: isLoadingJackpotPrize } = useReadContract({
    address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
    abi: DAILY_SPIN_ABI,
    chainId: dailySpinChainId,
    functionName: 'getWeeklyJackpot',
    query: {
      refetchOnMount: false,
    },
  });
  // data is an array of [weekNumber, jackpotPrize, requiredStreak] - all values need to be parsed
  const [, jackpotPrize] = data ?? [];
  return (
    <DialogRoot open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader flexDir="column">
          <Heading size="4xl" fontWeight="700">
            Spin Rewards
          </Heading>
        </DialogHeader>
        <DialogBody>
          <Flex rounded="16px" position="relative" overflow="hidden" w="full" py={4} px={6} color="fg" mb={2}>
            <LottieReact
              src="https://cdn.lottielab.com/l/5Va6N3tMn4Ke4s.json"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                minWidth: '100%',
                minHeight: '100%',
                aspectRatio: 1036 / 174,
                zIndex: 0,
              }}
            />
            <Heading
              fontSize="lg"
              fontWeight="500"
              lineHeight="24px"
              maxW="320px"
              mx="auto"
              textAlign="center"
              position="relative"
              zIndex={1}
            >
              Jackpot increases each week up to <br />
              <b>35,000 PLUME</b>
            </Heading>
          </Flex>
          <Card.Root rounded="2xl" bgColor="#E9F0F3" mb={4}>
            <Card.Body>
              <Heading fontSize="md" fontWeight="500" color="#207B9B" textAlign="center">
                All spin costs go back into rewards
              </Heading>
            </Card.Body>
          </Card.Root>
          <VStack alignItems="stretch" gap={4}>
            <RewardCard
              iconPath={REWARD_CATEGORY_METADATA[RewardCategory.Nothing].icon}
              title="No Reward"
              description="Try your luck next time!"
            />
            <Separator />
            <RewardCard
              iconPath="/assets/daily-spin/lightning-bolt-reward.svg"
              title="10 Day Streak"
              description="Number of consecutive days you've spun the Daily Spin wheel"
            />
            <Separator />
            <RewardCard
              iconPath={REWARD_CATEGORY_METADATA[RewardCategory.PP].icon}
              title="Plume PP"
              description="Random amount of PP. Awarded at midnight UTC."
            />
            <Separator />
            <RewardCard
              iconPath={REWARD_CATEGORY_METADATA[RewardCategory.RaffleTicket].icon}
              title="Raffle Tickets"
              description="Win raffle tickets (8 * your daily streak). Use the tickets in raffles for a chance to win RWA prizes"
            />
            <Separator />
            <RewardCard
              iconPath={REWARD_CATEGORY_METADATA[RewardCategory.PlumeToken].icon}
              title="Plume Tokens"
              description="Random amount of Plume tokens"
            />
            <Separator />
            <RewardCard
              iconPath={REWARD_CATEGORY_METADATA[RewardCategory.Jackpot].icon}
              title={`This week’s prize: ${formatNumberToLocale(Number(jackpotPrize ?? 0))} $PLUME 🔥`}
              loading={isLoadingJackpotPrize}
              description="Your chances increase as the week goes on, ending at Sunday midnight UTC."
            />
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

const RewardCard = ({
  title,
  description,
  iconPath,
  loading = false,
}: {
  title: string;
  description: string;
  slotRight?: number;
  iconPath: string;
  loading?: boolean;
}) => (
  <HStack justifyContent="space-between">
    <HStack gap={4}>
      <Image src={iconPath} boxSize={10} />
      <VStack alignItems="start" gap={0}>
        <Skeleton loading={loading}>
          <Heading size="lg" fontWeight="500">
            {title}
          </Heading>
        </Skeleton>
        <Text color="fg.muted" fontWeight="500" fontSize="md" letterSpacing="-0.18px">
          {description}
        </Text>
      </VStack>
    </HStack>
  </HStack>
);
